import { useEffect, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Map, Navigation, BarChart3 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { WardListTab } from '@/components/wards/WardListTab';
import { useUIStore } from '@/stores/uiStore';
import { VI_MESSAGES } from '@/constants/vietnamese';
import { SkeletonLoader } from '@/components/common/SkeletonLoader';
import logo from '/logo.png';

// Lazy load non-default tabs for better initial performance
const DirectionsTab = lazy(() => import('@/components/directions/DirectionsTab').then(module => ({ default: module.DirectionsTab })));
const ComparisonTab = lazy(() => import('@/components/comparison/ComparisonTab').then(module => ({ default: module.ComparisonTab })));

export function LeftPanel() {
  const { isPanelOpen, activeTab, togglePanel, setActiveTab, panelWidth, isMobile } = useUIStore();
  const prevIsMobileRef = useRef(isMobile);

  // Handle viewport transitions (desktop → mobile)
  useEffect(() => {
    // Only close panel when transitioning FROM desktop TO mobile
    if (!prevIsMobileRef.current && isMobile && isPanelOpen) {
      togglePanel();
    }
    
    // Update the ref with current value for next render
    prevIsMobileRef.current = isMobile;
  }, [isMobile, isPanelOpen, togglePanel]);

  // Initial setup - close panel on first mount if mobile
  useEffect(() => {
    if (isMobile && isPanelOpen) {
      togglePanel();
    }
  }, []); // Only run once on mount

  return (
    <AnimatePresence mode="wait">
      {isPanelOpen && (
        <motion.aside
          initial={{ x: -panelWidth }}
          animate={{ x: 0 }}
          exit={{ x: -panelWidth }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="relative z-10 h-full bg-white shadow-xl"
          style={{ width: panelWidth }}
        >
          {/* App Title and Toggle */}
          <div className="bg-blue-600 text-white">
            <div className="flex items-start justify-between px-4 py-3">
              <div className="flex items-center gap-3 flex-1">
                <img 
                  src={logo} 
                  alt="Logo" 
                  className="h-10 w-10 object-contain flex-shrink-0"
                />
                <div>
                  <h1 className="text-xl font-bold">{VI_MESSAGES.APP.TITLE}</h1>
                  <p className="text-xs opacity-90">{VI_MESSAGES.APP.SUBTITLE}</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePanel}
                className="h-8 w-8 text-white hover:bg-blue-700 hover:text-white"
                aria-label={VI_MESSAGES.PANEL.TOGGLE}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as any)}
            className="flex h-[calc(100%-5rem)] flex-col"
          >
            <TabsList className="grid h-12 w-full grid-cols-3 rounded-none bg-gray-50">
              <TabsTrigger
                value="wards"
                className="tab-trigger"
              >
                <Map className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.WARDS}</span>
              </TabsTrigger>
              <TabsTrigger
                value="directions"
                className="tab-trigger"
              >
                <Navigation className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.DIRECTIONS}</span>
              </TabsTrigger>
              <TabsTrigger
                value="comparison"
                className="tab-trigger"
              >
                <BarChart3 className="h-4 w-4" />
                <span className="hidden sm:inline">{VI_MESSAGES.TABS.COMPARISON}</span>
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1">
              <TabsContent value="wards" className="m-0 h-full">
                <WardListTab />
              </TabsContent>
              <TabsContent value="directions" className="m-0 h-full">
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <DirectionsTab />
                </Suspense>
              </TabsContent>
              <TabsContent value="comparison" className="m-0 h-full">
                <Suspense fallback={<SkeletonLoader count={3} />}>
                  <ComparisonTab />
                </Suspense>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </motion.aside>
      )}

      {/* Collapsed State - Toggle Button */}
      {!isPanelOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute left-0 top-4 z-10"
        >
          <Button
            variant="default"
            size="sm"
            onClick={togglePanel}
            className="rounded-l-none bg-blue-600 text-white hover:bg-blue-700 shadow-lg border border-blue-600"
            aria-label={VI_MESSAGES.PANEL.TOGGLE}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
